class PawTempoProduct extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.form = this.querySelector('[data-product-form]');
    this.submitButton = this.querySelector('[data-product-submit]');
    this.fileInput = this.querySelector('[data-photo-input]');
    this.fileName = this.querySelector('[data-photo-name]');
    this.fileHelp = this.querySelector('[data-photo-help]');
    this.preview = this.querySelector('[data-photo-preview]');
    this.uploadIcon = this.querySelector('[data-upload-icon]');
    this.variantVideos = [...this.querySelectorAll('[data-variant-video]')];
    this.motionOptionName = this.dataset.motionOptionName || 'Swing Style';
    this.variantInput = this.querySelector('[data-variant-id]');
    this.priceNodes = this.querySelectorAll('[data-product-price], [data-submit-price], [data-mobile-price]');
    this.comparePrice = this.querySelector('[data-compare-price]');
    this.stockMessage = this.querySelector('[data-stock-message]');
    this.optionSelects = [...this.querySelectorAll('[data-option-select]')];
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
    this.defaultSubmitLabel = this.submitButton?.querySelector('[data-submit-label]')?.textContent?.trim() || 'Add to cart';

    const variantsEl = this.querySelector('[data-variants-json]');
    if (variantsEl) {
      try { this.variants = JSON.parse(variantsEl.textContent); } catch (error) { this.variants = []; }
    } else {
      this.variants = [];
    }

    this.setupGallery();

    if (this.fileInput) this.fileInput.addEventListener('change', () => this.onPhotoChange());
    this.optionSelects.forEach((select) => select.addEventListener('change', () => this.onVariantChange()));

    this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
    this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.changeQuantity(1));

    // Keep Shopify's native product-form submission untouched. Variant videos
    // load only after a shopper changes an option, protecting initial load time.
  }

  setupGallery() {
    this.galleryMain = this.querySelector('[data-gallery-main]');
    this.gallerySlides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.galleryThumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.galleryThumbTrack = this.querySelector('[data-gallery-thumbs]');
    this.galleryCounter = this.querySelector('[data-gallery-counter]');
    this.galleryCurrent = this.querySelector('[data-gallery-current]');
    this.galleryIndex = Math.max(0, this.gallerySlides.findIndex((slide) => !slide.hidden));

    this.galleryThumbs.forEach((button) => {
      button.addEventListener('click', () => this.onGalleryChange(button));
    });
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.showGalleryIndex(this.galleryIndex - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showGalleryIndex(this.galleryIndex + 1));
    this.querySelector('[data-thumbs-prev]')?.addEventListener('click', () => this.scrollThumbnails(-1));
    this.querySelector('[data-thumbs-next]')?.addEventListener('click', () => this.scrollThumbnails(1));

    if (this.galleryMain) {
      let touchStartX = 0;
      let touchStartY = 0;
      this.galleryMain.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches?.[0];
        if (!touch) return;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      }, { passive: true });
      this.galleryMain.addEventListener('touchend', (event) => {
        const touch = event.changedTouches?.[0];
        if (!touch || this.gallerySlides.length < 2) return;
        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;
        if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
        this.showGalleryIndex(this.galleryIndex + (deltaX < 0 ? 1 : -1));
      }, { passive: true });
      this.galleryMain.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showGalleryIndex(this.galleryIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showGalleryIndex(this.galleryIndex + 1);
        }
      });
    }

    this.showGalleryIndex(this.galleryIndex, { autoplay: false, scrollThumb: false });
  }

  onGalleryChange(button) {
    const index = Number(button.dataset.galleryIndex);
    if (!Number.isFinite(index)) return;
    this.showGalleryIndex(index);
  }

  showGalleryIndex(index, options = {}) {
    if (!this.gallerySlides?.length) return;
    const total = this.gallerySlides.length;
    const normalizedIndex = ((Number(index) % total) + total) % total;
    const activeSlide = this.gallerySlides[normalizedIndex];
    const autoplay = options.autoplay !== false;

    this.hideVariantVideos();
    this.gallerySlides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === normalizedIndex;
      slide.hidden = !isActive;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (!isActive) slide.querySelector('video')?.pause();
    });

    this.galleryThumbs.forEach((thumb, thumbIndex) => {
      const isActive = thumbIndex === normalizedIndex;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    this.galleryIndex = normalizedIndex;
    if (this.galleryCurrent) this.galleryCurrent.textContent = String(normalizedIndex + 1);
    if (this.galleryCounter) this.galleryCounter.hidden = false;

    const activeVideo = activeSlide?.querySelector('video');
    if (autoplay && activeVideo && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const playPromise = activeVideo.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    }

    if (options.scrollThumb !== false) {
      this.galleryThumbs[normalizedIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  scrollThumbnails(direction) {
    if (!this.galleryThumbTrack) return;
    this.galleryThumbTrack.scrollBy({ left: this.galleryThumbTrack.clientWidth * 0.82 * direction, behavior: 'smooth' });
  }

  onPhotoChange() {
    const file = this.fileInput?.files?.[0];
    if (!file) return;
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      this.fileInput.value = '';
      this.fileInput.setCustomValidity('Please choose a photo smaller than 15MB.');
      this.fileInput.reportValidity();
      this.fileInput.setCustomValidity('');
      return;
    }
    if (this.fileName) this.fileName.textContent = file.name;
    if (this.fileHelp) this.fileHelp.textContent = 'Photo added · Click to replace';
    if (this.preview) {
      if (this.preview.dataset.objectUrl) URL.revokeObjectURL(this.preview.dataset.objectUrl);
      const objectUrl = URL.createObjectURL(file);
      this.preview.dataset.objectUrl = objectUrl;
      this.preview.src = objectUrl;
      this.preview.hidden = false;
    }
    if (this.uploadIcon) this.uploadIcon.hidden = true;
    this.querySelector('[data-upload-box]')?.classList.add('is-complete');
  }

  onVariantChange() {
    if (!this.variants?.length || !this.optionSelects.length) return;
    const selected = this.optionSelects
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((select) => select.value);

    const variant = this.variants.find((item) => Array.isArray(item.options) && item.options.every((value, index) => value === selected[index]));
    if (!variant) {
      if (this.submitButton) this.submitButton.disabled = true;
      return;
    }

    if (this.variantInput) this.variantInput.value = variant.id;
    const price = this.formatMoney(variant.price);
    this.priceNodes.forEach((node) => { node.textContent = price; });

    if (this.comparePrice) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePrice.textContent = this.formatMoney(variant.compare_at_price);
        this.comparePrice.hidden = false;
      } else {
        this.comparePrice.hidden = true;
        this.comparePrice.textContent = '';
      }
    }

    const label = this.submitButton?.querySelector('[data-submit-label]');
    if (this.submitButton) this.submitButton.disabled = !variant.available;
    if (label) label.textContent = variant.available ? this.defaultSubmitLabel : 'Sold out';

    if (this.stockMessage) {
      this.stockMessage.innerHTML = variant.available ? '<span aria-hidden="true"></span>Available to order' : 'Sold out';
      this.stockMessage.classList.toggle('is-sold-out', !variant.available);
    }

    const videoIsActive = this.syncVariantVideo();

    if (!videoIsActive) {
      const featuredMediaId = variant.featured_media?.id || variant.featured_image?.id;
      const mediaIndex = this.gallerySlides?.findIndex((slide) => String(slide.dataset.productMediaId) === String(featuredMediaId));
      if (mediaIndex >= 0) this.showGalleryIndex(mediaIndex);
    }
  }

  syncVariantVideo() {
    if (!this.variantVideos.length) return false;

    const normalizedOptionName = this.normalizeValue(this.motionOptionName);
    const motionSelect = this.optionSelects.find((select) => {
      const label = select.closest('.pt-option-field')?.querySelector(':scope > span')?.textContent || select.getAttribute('aria-label') || '';
      return this.normalizeValue(label) === normalizedOptionName;
    }) || this.optionSelects.find((select) => {
      const values = [...select.options].map((option) => this.normalizeValue(option.value));
      return this.variantVideos.some((item) => values.includes(this.normalizeValue(item.dataset.optionValue)));
    });

    const selectedValue = this.normalizeValue(motionSelect?.value);
    const activeItem = this.variantVideos.find((item) => this.normalizeValue(item.dataset.optionValue) === selectedValue);

    this.hideVariantVideos(activeItem);
    if (!activeItem) {
      this.showGalleryIndex(this.galleryIndex, { autoplay: false, scrollThumb: false });
      return false;
    }

    this.gallerySlides?.forEach((slide) => {
      slide.hidden = true;
      slide.setAttribute('aria-hidden', 'true');
      slide.querySelector('video')?.pause();
    });
    activeItem.hidden = false;
    activeItem.setAttribute('aria-hidden', 'false');
    const video = activeItem.querySelector('video');
    if (video && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    }
    this.galleryThumbs?.forEach((item) => {
      item.classList.remove('is-active');
      item.removeAttribute('aria-current');
    });
    if (this.galleryCounter) this.galleryCounter.hidden = true;
    return true;
  }

  hideVariantVideos(exceptItem = null) {
    this.variantVideos.forEach((item) => {
      if (item === exceptItem) return;
      item.hidden = true;
      item.setAttribute('aria-hidden', 'true');
      item.querySelector('video')?.pause();
    });
  }

  normalizeValue(value) {
    return String(value || '').trim().toLowerCase();
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2
      }).format(Number(cents) / 100);
    } catch (error) {
      return `$${(Number(cents) / 100).toFixed(2).replace(/\.00$/, '')}`;
    }
  }

  changeQuantity(delta) {
    const input = this.querySelector('[data-qty-input]');
    if (!input) return;
    const min = Number(input.min || 1);
    const current = Number(input.value || min);
    input.value = Math.max(min, current + delta);
  }

}

if (!customElements.get('pawtempo-product')) {
  customElements.define('pawtempo-product', PawTempoProduct);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-scroll-to-product]');
  if (!trigger) return;
  const target = document.querySelector('[data-product-form-anchor]') || document.querySelector('[data-product-anchor]');
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

document.documentElement.classList.remove('no-js');
